import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, FileSearch, Loader2 } from "lucide-react";

/**
 * VM-10 — Seite „Dokument prüfen".
 * 2026-03 — jetzt auch OHNE Login erreichbar. Bei anonymem Zugriff wird
 * der öffentliche Endpoint `/letters/verify-public` genutzt, der weniger
 * interne IDs zurückgibt (keine linked_contact_id etc.).
 */
export default function LetterVerify() {
  const { user } = useAuth();
  const isAuthed = !!user;
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!sig.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const url = isAuthed ? "/letters/verify" : "/letters/verify-public";
      const { data } = await api.post(url, { signature_id: sig.trim() });
      setResult(data);
      toast.success("Signatur verifiziert");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Signatur konnte nicht verifiziert werden";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="letter-verify-page">
      <div className="flex items-start gap-3">
        <FileSearch className="w-8 h-8 text-slate-500 mt-1" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Dokument prüfen</h1>
          <p className="text-sm text-slate-600 mt-1">
            Jedes von Domivus versendete Schreiben enthält eine eindeutige, verschlüsselt
            signierte Kennung im Format <code className="font-mono">DOMIVUS-JAHR-000000-XXXXX</code>.
            Gib die ID hier ein, um Ursprung, Empfänger und Versandkanal zu prüfen.
          </p>
        </div>
      </div>

      <Card className="p-5">
        <form onSubmit={submit} className="space-y-3">
          <Label htmlFor="sig-input" className="text-sm font-medium">Signatur-ID</Label>
          <div className="flex gap-2">
            <Input
              id="sig-input"
              data-testid="verify-sig-input"
              value={sig}
              onChange={(e) => setSig(e.target.value)}
              placeholder="DOMIVUS-2026-000047-a7b3f"
              className="font-mono text-sm"
              autoFocus
            />
            <Button type="submit" disabled={busy || !sig.trim()} data-testid="verify-sig-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Prüfen"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            Findest du die ID im PDF unten links (sehr klein, hellgrau) oder in den Dokumenteigenschaften (Adobe: Datei → Eigenschaften → Beschreibung → Schlüsselwörter).
          </p>
        </form>
      </Card>

      {error && (
        <Card className="p-5 border-red-200 bg-red-50" data-testid="verify-error">
          <div className="flex items-start gap-2 text-red-800">
            <ShieldAlert className="w-5 h-5 mt-0.5" />
            <div className="text-sm">{error}</div>
          </div>
        </Card>
      )}

      {result && (
        <Card className="p-5 border-emerald-200 bg-emerald-50" data-testid="verify-result">
          <div className="flex items-start gap-2 mb-4">
            <ShieldCheck className="w-6 h-6 text-emerald-700" />
            <div>
              <div className="text-sm font-semibold text-emerald-900">Signatur gültig</div>
              <div className="font-mono text-xs text-emerald-800 mt-0.5">{result.signature_id}</div>
            </div>
          </div>
          <dl className="grid grid-cols-[140px_1fr] gap-y-2 gap-x-3 text-sm">
            <dt className="text-slate-600">Erstellt am</dt>
            <dd className="text-slate-900">{new Date(result.created_at).toLocaleString("de-DE")}</dd>
            <dt className="text-slate-600">Betreff</dt>
            <dd className="text-slate-900">{result.subject || "—"}</dd>
            <dt className="text-slate-600">Empfänger</dt>
            <dd className="text-slate-900 whitespace-pre-wrap">{result.recipient_summary || "—"}</dd>
            <dt className="text-slate-600">Absender</dt>
            <dd className="text-slate-900">{result.sender_summary || "—"}</dd>
            <dt className="text-slate-600">Versandkanal</dt>
            <dd className="text-slate-900">{(result.channels || []).join(", ") || "—"}</dd>
            {result.linked_tenancy_id && (<>
              <dt className="text-slate-600">Mietverhältnis</dt>
              <dd className="text-slate-900 font-mono text-xs">{result.linked_tenancy_id}</dd>
            </>)}
            {result.linked_contact_id && (<>
              <dt className="text-slate-600">Kontakt</dt>
              <dd className="text-slate-900 font-mono text-xs">{result.linked_contact_id}</dd>
            </>)}
            {result.linked_property_id && (<>
              <dt className="text-slate-600">Objekt</dt>
              <dd className="text-slate-900 font-mono text-xs">{result.linked_property_id}</dd>
            </>)}
          </dl>
        </Card>
      )}
    </div>
  );
}
